import { CircularProgress } from '@mui/material';
import { Box } from '@mui/system';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';

import { decryptInitPayloadService } from '../../api/encryption';
import { ConsumerLoanTargetPath } from '../../domain/types';
import { ConsumerLoanParameter } from '../../domain/types/ConsumerLoanParameter';
import { getPath } from '../../helpers/url';
import { useAppDispatch } from '../../hooks';
import useSettings from '../../hooks/useSettings';
import { createNewApplication } from '../../store/application/application';
import { addOrUpdateFormField } from '../../store/applicationform';
import { invalidateStore } from '../../store/store';
import { setCurrentPage } from '../../store/wizard/wizard';

const InitPage = () => {
    const dispatch = useAppDispatch();
    const settings = useSettings();
    const navigate = useNavigate();
    const [params] = useSearchParams();

    const initApplication = async (payload: string) => {
        try {
            await invalidateStore(dispatch);
            const init = await decryptInitPayloadService(settings.proxyBaseUrl, settings.apiKey, payload);
            await dispatch(createNewApplication()).unwrap();

            if (init.tenor && init.amount) {
                await dispatch(setCurrentPage(0));
                await dispatch(
                    addOrUpdateFormField({
                        fieldName: 'amount',
                        value: init.amount,
                    }),
                );
                await dispatch(
                    addOrUpdateFormField({
                        fieldName: 'tenor',
                        value: init.tenor,
                    }),
                );
                await dispatch(
                    addOrUpdateFormField({
                        fieldName: 'applicantGovernmentId',
                        value: init.governmentId,
                    }),
                );
                await dispatch(
                    addOrUpdateFormField({
                        fieldName: 'useConsolidation',
                        value: init.useConsolidation,
                    }),
                );
                navigate(getPath(ConsumerLoanTargetPath.application, settings.languageCode, settings.countryCode));
            } else {
                navigate(getPath(ConsumerLoanTargetPath.calculator, settings.languageCode, settings.countryCode));
            }
        } catch (error) {
            throw new Error('Could not init application: ' + settings.proxyBaseUrl + ' ' + payload + ' ' + error, { cause: error });
        }
    };

    useEffect(() => {
        if (!settings.proxyBaseUrl) {
            return;
        }

        const initPayloadQueryParam = params.get(ConsumerLoanParameter.initPayload);

        if (initPayloadQueryParam) {
            initApplication(initPayloadQueryParam);
        } else {
            navigate(getPath(ConsumerLoanTargetPath.calculator, settings.languageCode, settings.countryCode));
        }
    }, [params, settings, dispatch]);

    return (
        <Box display="flex" justifyContent="center" marginBottom={2} marginTop={2}>
            <CircularProgress />
        </Box>
    );
};

export default InitPage;
